// Choreography for the guided demo.
//
// Each beat is a short scene: the cursor goes somewhere, does something, and
// the card says why it mattered. Beats advance on the user's click, so this is
// a demo they drive rather than a video they sit through.
//
// The demo is five stages rather than one line. A shared start — the customer,
// the service, the utility — runs into a FORK on the Existing Load tab, where
// the app's real two-card chooser is on screen and the viewer picks the method
// they want to see: measured demand, or the traditional calculation. One path
// plays (existing load, new loads, results, each told that path's way), then
// an OFFER to watch the other method on the same house, then a shared ending
// through the report. The viewer's choices live in a DemoPlan; the script is
// rebuilt from the plan whenever it changes, and every beat before the choice
// keeps its position, so the plan can change without the viewer moving.
//
// Beats only ever move forward. Going Back does not undo anything — instead the
// whole sample job is rebuilt from scratch by replaying `settle` for every
// earlier beat (see demoStateBefore). That is what makes a revisited beat start
// from blank fields, what stops a second visit to the equipment beat adding a
// second copy of the same heat pump — and what shows the chooser again, with
// nothing picked, when the viewer steps back to the fork: the choice is
// replayed like everything else.
//
// Targets are the same `data-testid` attributes the Playwright harness uses, so
// a selector that goes stale here breaks a test somewhere too rather than
// silently pointing the cursor at nothing.

import type { Equipment, Project } from '../../types/project';
import { DEMO_CUSTOMER_EMAIL, demoInventorySeed } from '../../lib/demoProject';
import {
  measuredMethodologyVersion,
  traditionalMethodologyVersion,
} from '../../lib/calc/citations';

export type DemoTab = 'project' | 'service' | 'ami' | 'equipment' | 'results';

export const DEMO_TAB_LABELS: Record<DemoTab, string> = {
  project: 'Project Info',
  service: 'Service Info',
  ami: 'Existing Load',
  equipment: 'New Loads',
  results: 'Results',
};

/** The two ways the Existing Load tab establishes the existing load. */
export type DemoPath = 'measured' | 'traditional';

export const DEMO_PATH_LABELS: Record<DemoPath, string> = {
  measured: 'Measured demand',
  traditional: 'Traditional calculation',
};

export function otherPath(path: DemoPath): DemoPath {
  return path === 'measured' ? 'traditional' : 'measured';
}

/**
 * What the viewer has chosen so far.
 *
 * `first` is null until a card is clicked at the fork; the script then runs
 * only as far as the fork. `second` is set when the offer to see the other
 * method is taken, and cleared when it is declined.
 */
export interface DemoPlan {
  first: DemoPath | null;
  second?: DemoPath | null;
}

export interface DemoContext {
  /** Glide the cursor onto an element (scrolling it into view first). */
  moveTo(selector: string, offset?: { x?: number; y?: number }): Promise<void>;
  /** Move, then press — ripple and all. `real` also fires the element's click. */
  click(selector: string, opts?: { x?: number; y?: number; real?: boolean }): Promise<void>;
  /** Draw attention to something the cursor cannot type into. */
  pulse(selector: string): Promise<void>;
  /** Beat a ring around something too small or inline to scale — a word, a figure. */
  ring(selector: string): Promise<void>;
  /**
   * A mark that stays put for the rest of the beat, tracking its element.
   *
   * For a control the user is invited to go and press themselves — it survives
   * them opening it and coming back — and for holding two related things lit at
   * once, like the season that decided a call and the column showing the call.
   */
  mark(selector: string, opts?: { tone?: 'red' | 'blue'; shape?: 'oval' | 'box' }): Promise<void>;
  /**
   * Opens a stand-in for a native dropdown, walks the cursor down the real
   * options, and picks one. A browser will not let a <select> be shown open
   * under script control, and "the value simply changed" is not a demo.
   */
  chooseOption(selector: string, label: string, apply: (value: string) => void): Promise<void>;
  /** Animates a file onto a drop zone, the way a user dragging one would look. */
  dropFile(selector: string, fileName: string): Promise<void>;
  /** Focus a field and enter text a character at a time. */
  type(selector: string, text: string, apply: (value: string) => void): Promise<void>;
  sleep(ms: number): Promise<void>;
  /** Resolves false if the element never appears. */
  waitFor(selector: string, timeoutMs?: number): Promise<boolean>;
  patch(updates: Partial<Project>): void;
  setTab(tab: DemoTab): void;
  /** Loads the sample year of interval data and the fields an upload would set. */
  seedAmi(): void;
  addEquipment(): void;
  updateEquipment(updates: Partial<Equipment>): void;
  /**
   * Adds a dwelling-inventory row, or updates the one already carrying that
   * name — the same upsert the Dwelling Profile checklist performs, which is
   * what keeps a replayed checklist beat from listing the range twice.
   */
  addExisting(seed: Partial<Equipment>): void;
  /** Which sub-path of the Existing Load tab is open — the real switch's state. */
  setMode(mode: DemoPath | null): void;
}

/** The subset a beat may use once its animation no longer matters. */
export type DemoSettleContext = Pick<
  DemoContext,
  'patch' | 'updateEquipment' | 'seedAmi' | 'addEquipment' | 'addExisting' | 'setMode'
>;

export interface DemoBeat {
  id: string;
  tab: DemoTab;
  title: string;
  body: string;
  /** Small highlighted line under the copy — used for real-world timing notes. */
  aside?: string;
  /**
   * An optional nudge to try something. Never required and never blocking: a
   * real click pauses the demo, the user pokes around, and Resume picks the
   * scene back up.
   */
  invite?: string;
  /**
   * Modals this beat expects to still be open when it starts, by testid.
   *
   * Entering a beat closes anything a previous one opened, which is what stops
   * a dialog stranding the user. The report is the exception: the beat that
   * opens it and the beat that talks about it are different scenes, so without
   * this the report would be closed the instant it was reached.
   */
  keeps?: string[];
  /**
   * Force the card to the top of the screen for this beat.
   *
   * It normally moves only when the cursor would arrive underneath it, which is
   * not enough when the thing being discussed sits along the bottom edge — the
   * report's action bar was hidden behind the card the whole time it was being
   * described.
   */
  card?: 'top';
  /**
   * The viewer, not Next, moves the demo on from this beat.
   *
   * 'path'  — the fork. The two Existing Load cards are on screen and the demo
   *           waits for one to be clicked; Next is hidden.
   * 'offer' — the card offers the other method on the same house, or the report.
   */
  awaits?: 'path' | 'offer';
  run(ctx: DemoContext): Promise<void>;
  /**
   * The beat's end state, applied instantly.
   *
   * Serves two jobs: it finishes a scene the user skipped past, and it is
   * replayed to rebuild the job when navigating. Must be idempotent — it is
   * run again every time any later beat is entered.
   */
  settle?(ctx: DemoSettleContext): void;
}

export interface DemoSeed {
  /** Preparer block, from the signed-in user's Company Profile where they have one. */
  preparer: Partial<Project>;
  utilityId: string | null;
  utilityName: string;
}

/** A sample job rebuilt to the point just before `index`. */
export interface DemoState {
  project: Partial<Project>;
  /** Proposed new loads — the New Loads tab. */
  equipment: Partial<Equipment>[];
  /** Dwelling-inventory rows — the traditional path's side of the Existing Load tab. */
  existing: Partial<Equipment>[];
  ami: boolean;
  /** Which sub-path of the Existing Load tab is open. Null shows the chooser. */
  mode: DemoPath | null;
}

/**
 * Replays every beat before `index` to get the state the demo should be in
 * when that beat begins. Beats are never asked to undo themselves; the job is
 * simply rebuilt, which is both simpler and impossible to get half-right.
 */
export function demoStateBefore(beats: DemoBeat[], index: number): DemoState {
  const project: Partial<Project> = {};
  const equipment: Partial<Equipment>[] = [];
  const existing: Partial<Equipment>[] = [];
  let ami = false;
  let mode: DemoPath | null = null;
  const ctx: DemoSettleContext = {
    patch: (updates) => Object.assign(project, updates),
    addEquipment: () => {
      equipment.push({});
    },
    // Mirrors the app's upsert: filling with no row present creates one.
    updateEquipment: (updates) => {
      if (equipment.length === 0) equipment.push({});
      Object.assign(equipment[equipment.length - 1], updates);
    },
    // Mirrors the checklist: one row per item name, however often it is set.
    addExisting: (seed) => {
      const row = existing.find((r) => r.equipment_name === seed.equipment_name);
      if (row) Object.assign(row, seed);
      else existing.push({ ...seed });
    },
    seedAmi: () => {
      ami = true;
    },
    setMode: (m) => {
      mode = m;
    },
  };
  for (let i = 0; i < Math.min(index, beats.length); i++) beats[i].settle?.(ctx);
  return { project, equipment, existing, ami, mode };
}

const HEAT_PUMP: Partial<Equipment> = {
  equipment_name: 'Heat Pump — Main Floor',
  equipment_type: 'Heat Pump',
  source: 'Nameplate',
  amps: 24,
  voltage: 240,
  quantity: 1,
};

const WATER_HEATER: Partial<Equipment> = {
  equipment_name: 'Heat Pump Water Heater',
  equipment_type: 'Water Heater',
  source: 'Nameplate',
  amps: 15,
  voltage: 240,
  quantity: 1,
};

/**
 * The sample job is in Vermont, which enforces the 2023 code — so the section
 * numbers the cards quote, 220.87 and 220.83, are the sample's own, and the
 * edition beat is where Massachusetts and 120.83 get explained.
 */
export const DEMO_STATE = 'VT';
const SAMPLE_PROJECT: Partial<Project> = { customer_state: DEMO_STATE };

/** The stored methodology line for a path — what ExistingLoadStep.chooseMode writes. */
function versionFor(path: DemoPath): string {
  return path === 'measured'
    ? measuredMethodologyVersion(SAMPLE_PROJECT)
    : traditionalMethodologyVersion(SAMPLE_PROJECT);
}

/** The house the traditional path describes, by Dwelling Profile checklist key. */
export const DEMO_FLOOR_AREA_SQFT = 1800;
export const DEMO_INVENTORY = ['range', 'dishwasher', 'water-heater', 'dryer'] as const;
/** Added on its own beat: a fuel-fired furnace still has a blower and controls. */
export const DEMO_INVENTORY_HEATING = 'furnace-gas';

/**
 * Opens the report, or does nothing if it is already up.
 *
 * Two beats need it on screen: the one that generates it and the one that
 * describes it. Navigating back into the second would otherwise leave its copy
 * talking about a document that is not there.
 */
async function ensureReportOpen(ctx: DemoContext, pace: boolean) {
  if (await ctx.waitFor('[data-testid="report-modal"]', 150)) return;
  await ctx.click('[data-testid="btn-generate-report"]', { real: true });
  if (await ctx.waitFor('[data-testid="btn-generate-report-confirm"]', 3000)) {
    if (pace) await ctx.sleep(1100);
    await ctx.click('[data-testid="btn-generate-report-confirm"]', { real: true });
  }
  await ctx.waitFor('[data-testid="report-modal"]', 4000);
}

// ---------------------------------------------------------------------------
// Stage 1 — the shared start: the customer, the service, the utility.
// ---------------------------------------------------------------------------

function trunkBeats(seed: DemoSeed): DemoBeat[] {
  return [
    {
      id: 'orientation',
      tab: 'project',
      title: 'A whole evaluation, start to finish',
      body:
        'This is a sample job — a real address, a real utility, real-shaped numbers — run ' +
        'end to end. Nothing here touches your own work, and nothing is saved. Watch it get ' +
        'filled in.',
      async run(ctx) {
        await ctx.sleep(400);
      },
    },
    {
      id: 'customer',
      tab: 'project',
      title: 'Start with who and where',
      body:
        'An evaluation belongs to one specific property, so this is the address everything ' +
        'that follows is tied to — and what identifies the job when you come back to it.',
      async run(ctx) {
        await ctx.type('[data-testid="customer-name"]', 'Dave Smith', (v) =>
          ctx.patch({ customer_name: v }),
        );
        await ctx.type('[data-testid="customer-address"]', '45 Elm Street', (v) =>
          ctx.patch({ customer_address: v }),
        );
        // The authorization request emails the customer, so it needs this.
        await ctx.type('[data-testid="customer-email"]', DEMO_CUSTOMER_EMAIL, (v) =>
          ctx.patch({ customer_email: v }),
        );
      },
      settle(ctx) {
        ctx.patch({
          customer_name: 'Dave Smith',
          customer_address: '45 Elm Street',
          customer_email: DEMO_CUSTOMER_EMAIL,
        });
      },
    },
    {
      id: 'zip',
      tab: 'project',
      title: 'The ZIP does extra work',
      body:
        'Beyond printing on the report, it is how TrueLoad works out which utility serves ' +
        'the property — which is what makes the data request a couple of tabs along possible. ' +
        'The state does a job too: it sets which edition of the code the report cites.',
      async run(ctx) {
        await ctx.type('[data-testid="customer-city"]', 'Burlington', (v) =>
          ctx.patch({ customer_city: v }),
        );
        await ctx.type('[data-testid="customer-state"]', DEMO_STATE, (v) =>
          ctx.patch({ customer_state: v }),
        );
        await ctx.type('[data-testid="customer-zip"]', '05401', (v) =>
          ctx.patch({ customer_zip: v }),
        );
      },
      settle(ctx) {
        ctx.patch({ customer_city: 'Burlington', customer_state: DEMO_STATE, customer_zip: '05401' });
      },
    },
    {
      id: 'preparer',
      tab: 'project',
      title: 'Who worked it out',
      body:
        'Whoever runs the evaluation goes on the report — your name, your company, and a ' +
        'license number if you hold one. Plenty of installers use TrueLoad to size up a ' +
        'job without a permit ever being involved. It fills in from your Company Profile, ' +
        'so this is a one-time setup.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="prepared-by"]');
        ctx.patch(seed.preparer);
        await ctx.pulse('[data-testid="prepared-by"]');
        await ctx.sleep(250);
      },
      settle(ctx) {
        ctx.patch(seed.preparer);
      },
    },
    {
      id: 'header',
      tab: 'project',
      title: 'Your controls up here',
      body:
        'Projects holds every job as its own separate record. Save whenever you want to be ' +
        'certain nothing is lost. And the gear opens your settings — your Company Profile ' +
        'and your subscription both live in there.',
      invite: 'open any of them and have a look — the ring stays put until you come back.',
      async run(ctx) {
        // A ring rather than a click: pressing these for real would open a
        // dialog over the demo, and a click that opens nothing looks broken.
        await ctx.mark('[data-testid="header-actions"]', { tone: 'red', shape: 'oval' });
      },
    },
    {
      id: 'tabs',
      tab: 'project',
      title: 'And the job runs left to right',
      body:
        'Each tab is one stage: who and where, the electrical service, the existing load, ' +
        'what you are adding, and the answer. You can move between them freely — the dots ' +
        'show which still owe you something, so a job you put down for a week picks back up ' +
        'where it left off.',
      invite: 'click through them and have a look around. The ring waits for you.',
      async run(ctx) {
        // The buttons, not the <nav> around them: that element runs the full
        // width of the window, so an oval drawn on it spanned the whole screen.
        await ctx.mark('[data-testid="tab-bar"] button', { tone: 'red', shape: 'oval' });
      },
    },
    {
      id: 'service-tab',
      tab: 'service',
      title: 'On to the service itself',
      body:
        'Everything so far described the property. This tab describes the electrical ' +
        'supply feeding it — what is already there, before anything new is hung off it.',
      async run(ctx) {
        await ctx.click('[data-testid="tab-service-info"]', { real: true });
        await ctx.sleep(700);
      },
    },
    {
      id: 'service-config',
      tab: 'service',
      title: 'How the service is wired',
      body:
        'This sets how capacity is worked out — a single-phase service is volts times ' +
        'amps, three-phase is about 73% more for the same numbers. Read it off the meter ' +
        'or the panel label rather than assuming: nearly every house is 120/240V ' +
        'single-phase, and picking a three-phase option for one would overstate what it ' +
        'can carry.',
      async run(ctx) {
        await ctx.chooseOption(
          '[data-testid="service-configuration"]',
          '120/240V single-phase',
          () => {
            // Already the sample's configuration; the beat exists to show how
            // the choice is made and why it matters.
          },
        );
      },
    },
    {
      id: 'service-rating',
      tab: 'service',
      title: 'The number you are checking against',
      body:
        'The main breaker rating is the whole comparison: the existing load plus the new ' +
        'load, against this. It is the number a service upgrade would replace — and the ' +
        'one the customer is hoping not to have to change.',
      async run(ctx) {
        await ctx.type('[data-testid="service-rating-amps"]', '200', (v) =>
          ctx.patch({ service_rating_amps: Number(v) || 0 }),
        );
        await ctx.sleep(300);
      },
      settle(ctx) {
        ctx.patch({ service_rating_amps: 200 });
      },
    },
    {
      id: 'utility',
      tab: 'service',
      title: 'Which utility serves it',
      body:
        'The customer’s utility holds the meter readings the measured method runs on, and ' +
        'the report names it either way. Utilities known to serve that ZIP are listed first ' +
        '— 45 Elm Street is in Burlington, so Burlington Electric is top of the list — and ' +
        'picking one turns on Get data on the Existing Load tab, which shows how TrueLoad gets its data.',
      async run(ctx) {
        await ctx.chooseOption('[data-testid="utility-id"]', seed.utilityName, () => {
          // Mirrors ServiceInfoForm's own handler: picking a utility sets the
          // id AND the data provider name. Patching only the id left the
          // authorization request reading "Not specified" for a utility that
          // had plainly been chosen.
          ctx.patch({ utility_id: seed.utilityId, ami_data_provider: seed.utilityName });
        });
        await ctx.sleep(400);
      },
      settle(ctx) {
        ctx.patch({ utility_id: seed.utilityId, ami_data_provider: seed.utilityName });
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Stage 2 — the fork. The app's own chooser, and a real click to move on.
// ---------------------------------------------------------------------------

function forkBeat(plan: DemoPlan): DemoBeat {
  return {
    id: 'existing-load-fork',
    tab: 'ami',
    awaits: 'path',
    title: 'Two ways to establish the existing load',
    body:
      'Everything from here rests on what the house already draws, and there are two ways ' +
      'to find out. Measured demand reads it off a year of the meter’s own record. The ' +
      'traditional calculation builds it from the house itself — floor area and appliances ' +
      '— with no utility data and no waiting. Both are always available; pick the one you ' +
      'want to see.',
    async run(ctx) {
      await ctx.click('[data-testid="tab-ami"]', { real: true });
      await ctx.sleep(700);
      // Lasting rings, not clicks: the click here is the viewer's to make.
      await ctx.mark('[data-testid="card-measured"]', { tone: 'red', shape: 'oval' });
      await ctx.mark('[data-testid="card-traditional"]', { tone: 'red', shape: 'oval' });
      await ctx.sleep(300);
    },
    // The viewer's click, replayed. Mirrors ExistingLoadStep.chooseMode: with
    // nothing on the other path yet, choosing a path also sets the report
    // basis, which is what makes the progress dots follow it at once.
    settle(ctx) {
      if (!plan.first) return;
      ctx.setMode(plan.first);
      ctx.patch({ report_method: plan.first, methodology_version: versionFor(plan.first) });
    },
  };
}

/**
 * Entering the second path on the same job. The chooser cards only exist
 * before a path is picked, so the way in is the switch at the top of the tab.
 */
function switchBeat(path: DemoPath): DemoBeat {
  const first = otherPath(path);
  return {
    id: `${path}-switch`,
    tab: 'ami',
    title: 'Same job, the other method',
    body:
      'Both paths live on one project. The switch at the top of this tab moves between ' +
      `them, and nothing already entered goes anywhere — the ${DEMO_PATH_LABELS[first].toLowerCase()} ` +
      'you just watched stays on the job while this one is added beside it.',
    async run(ctx) {
      await ctx.click('[data-testid="tab-ami"]', { real: true });
      await ctx.sleep(500);
      await ctx.click(`[data-testid="btn-mode-${path}"]`, { real: true });
      await ctx.sleep(600);
    },
    // Only the open sub-path: with the other path holding data, the real
    // switch leaves the report basis alone, and so does this.
    settle(ctx) {
      ctx.setMode(path);
    },
  };
}

// ---------------------------------------------------------------------------
// Stage 3a — the measured path.
// ---------------------------------------------------------------------------

function measuredExistingLoadBeats(second: boolean): DemoBeat[] {
  return [
    ...(second ? [switchBeat('measured')] : []),
    {
      id: 'ami-intro',
      tab: 'ami',
      title: 'Where the real numbers come from',
      body:
        'This is the heart of it. Rather than adding up every appliance and assuming the ' +
        'worst, NEC 220.87 lets a year of the meter’s own record stand in — what the house ' +
        'actually drew. Nothing loaded yet, so let us go and get it.',
      async run(ctx) {
        await ctx.sleep(600);
      },
    },
    {
      id: 'ami-request',
      tab: 'ami',
      title: 'Asking the utility for it',
      body:
        'You do not have to chase this down yourself. Get data opens one page with every ' +
        'way TrueLoad can get this utility’s interval data. This utility takes the request ' +
        'from its own customer, so TrueLoad emails the customer a link that writes it for ' +
        'them — their name, address and account details already filled in — and takes the ' +
        'file when the utility sends it back.',
      keeps: ['ami-request-modal'],
      async run(ctx) {
        await ctx.click('[data-testid="btn-request-ami"]', { real: true });
        await ctx.waitFor('[data-testid="ami-request-modal"]', 2500);
        await ctx.sleep(1100);
        // Safe to press: GetDataPage refuses to send for a demo project and
        // reports the same success the real path would.
        await ctx.click('[data-testid="btn-send-ami-request"]', { real: true });
        await ctx.waitFor('[data-testid="ami-request-sent"]', 3000);
        await ctx.sleep(600);
      },
    },
    {
      id: 'ami-wait',
      tab: 'ami',
      title: 'Then you wait — and that is fine',
      body:
        'This is the one point in a job where you stop and wait on somebody else. The ' +
        'project keeps everything you have entered, so you close it, get on with your week, ' +
        'and pick it up when the file lands — you get an email either way.',
      aside: 'In practice: days, not weeks, depending on how quickly the utility answers.',
      async run(ctx) {
        await ctx.mark('[data-testid="ami-request-sent"]', { tone: 'blue', shape: 'box' });
        await ctx.sleep(500);
      },
    },
    {
      id: 'ami-upload',
      tab: 'ami',
      title: 'The file arrives — drop it in',
      body:
        'However the utility sends it — CSV, Excel, a text export — your customer’s ' +
        'upload or yours goes straight in here. No reformatting, no stripping columns out first.',
      async run(ctx) {
        await ctx.sleep(500);
        await ctx.dropFile('[data-testid="ami-dropzone"]', 'BED_interval_data_45Elm.csv');
        await ctx.sleep(700);
        ctx.seedAmi();
        await ctx.sleep(1200);
      },
      settle(ctx) {
        ctx.seedAmi();
      },
    },
    {
      id: 'ami-parsed',
      tab: 'ami',
      title: 'And it reads itself',
      body:
        'TrueLoad works out the format, finds the single highest demand in the whole year, ' +
        'and reports the period it covers and how often the meter sampled. That peak is ' +
        'the number the entire evaluation rests on — it is worth a second look.',
      async run(ctx) {
        await ctx.ring('[data-testid="ami-data-points"]');
        await ctx.sleep(200);
        await ctx.ring('[data-testid="ami-summary-period"]');
      },
    },
    {
      id: 'ami-quality',
      tab: 'ami',
      title: 'Check this flag before you trust the rest',
      body:
        'A year of readings is only useful if it is intact. Data Ready in green means full ' +
        'coverage with no significant gaps. Amber means usable with something worth ' +
        'reading first; red means a gap or a frozen meter serious enough to fix before you ' +
        'rely on the answer.',
      async run(ctx) {
        await ctx.ring('[data-testid="ami-quality-badge"]');
        await ctx.sleep(300);
      },
    },
  ];
}

function measuredResultsBeats(second: boolean): DemoBeat[] {
  return [
    second ? basisBeat('measured') : verdictBeat(),
    {
      id: 'results-units',
      tab: 'results',
      title: 'Why everything is suddenly in kVA',
      body:
        'You entered amps on the equipment and the meter reported kW, but the comparison ' +
        'has to happen in one unit. kVA is that unit — volts times amps. A 200 A service ' +
        'at 240 V is 48 kVA, a 24 A heat pump at 240 V is 5.76 kVA, and the measured demand ' +
        'carries across as it stands. One column, all addable.',
      async run(ctx) {
        await ctx.ring('[data-testid="result-service-capacity"]');
        await ctx.sleep(300);
      },
    },
    {
      id: 'results-peak',
      tab: 'results',
      title: 'What the meter actually recorded',
      body:
        'The single highest demand in the whole year of readings. Not an estimate, not a ' +
        'sum of nameplates — the largest amount this house has genuinely drawn at once. ' +
        'Everything downstream is built on it.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="result-peak-demand"]');
        await ctx.ring('[data-testid="result-peak-demand"]');
        await ctx.sleep(300);
      },
    },
    {
      id: 'results-adder',
      tab: 'results',
      title: 'Then the code adds 25%',
      body:
        'NEC 220.87 multiplies that measured peak by 1.25 before anything new is added. ' +
        'That margin is the method’s built-in caution — cover for load creeping up and for ' +
        'a year that is not quite typical. You do not add a separate safety factor on top.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="result-existing-demand"]');
        await ctx.ring('[data-testid="result-existing-demand"]');
        await ctx.sleep(300);
      },
    },
    {
      id: 'results-newload',
      tab: 'results',
      title: 'Plus what you are adding',
      body:
        'Only the equipment that runs when the house peaks. Both of ours do, so both are ' +
        'counted at full rating — that is the concurrency call from the last tab turning ' +
        'into a number.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="result-new-load"]');
        await ctx.ring('[data-testid="result-new-load"]');
        await ctx.sleep(250);
        await ctx.ring('[data-testid="result-adjusted-demand"]');
        await ctx.sleep(250);
      },
    },
    {
      id: 'results-headroom',
      tab: 'results',
      title: 'How much room is left',
      body:
        'Utilisation is the share of the service this job consumes; the margin is what ' +
        'remains in kVA. That second figure is the one worth quoting — it answers the next ' +
        'question the customer asks, which is whether they can add something else later.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="result-utilization"]');
        await ctx.ring('[data-testid="result-utilization"]');
        await ctx.sleep(250);
        await ctx.ring('[data-testid="result-margin"]');
        await ctx.sleep(300);
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Stage 3b — the traditional path.
// ---------------------------------------------------------------------------

function traditionalExistingLoadBeats(second: boolean): DemoBeat[] {
  return [
    ...(second ? [switchBeat('traditional')] : []),
    {
      id: 'trad-occupancy',
      tab: 'ami',
      title: 'Does the method apply here?',
      body:
        'This calculation is written for a dwelling unit — a house, or one unit in a ' +
        'two-family or apartment building — on an ordinary single-phase service. TrueLoad ' +
        'asks first, because when the method does not apply it says so and points you at ' +
        'measured demand, which works for any existing service.',
      async run(ctx) {
        // A visual press plus the patch, rather than a real click: replaying
        // the scene must never toggle the answer back off.
        await ctx.click('[data-testid="occupancy-one-family"]');
        ctx.patch({ occupancy_type: 'one-family' });
        await ctx.sleep(600);
      },
      settle(ctx) {
        ctx.patch({ occupancy_type: 'one-family' });
      },
    },
    {
      id: 'trad-edition',
      tab: 'ami',
      title: 'Which edition of the code',
      body:
        'The section numbers on your report follow the edition your state enforces. ' +
        'Vermont is on the 2023 code, so this method is 220.83. Massachusetts enforces the ' +
        '2026 edition, where the same method is 120.83, and TrueLoad swaps the numbers for ' +
        'you. Change it here only if your inspector says otherwise.',
      async run(ctx) {
        await ctx.ring('[data-testid="code-edition"]');
        await ctx.sleep(300);
      },
    },
    {
      id: 'trad-floor-area',
      tab: 'ami',
      title: 'The house, in square feet',
      body:
        'Floor area becomes the lighting and receptacle load — three volt-amps for every ' +
        'square foot, measured by the outside dimensions. Leave out the garage, open porches ' +
        'and unfinished space.',
      async run(ctx) {
        await ctx.type('[data-testid="dwelling-floor-area"]', String(DEMO_FLOOR_AREA_SQFT), (v) =>
          ctx.patch({ dwelling_floor_area_sqft: Number(v) || null }),
        );
        await ctx.sleep(300);
      },
      settle(ctx) {
        ctx.patch({ dwelling_floor_area_sqft: DEMO_FLOOR_AREA_SQFT });
      },
    },
    {
      id: 'trad-circuits',
      tab: 'ami',
      title: 'The circuits every kitchen and laundry has',
      body:
        'Two small-appliance circuits and one laundry circuit are the code minimum, and ' +
        'each counts at 1,500 volt-amps whatever is plugged into it. They are filled in for ' +
        'you — change them only if the house genuinely has more.',
      async run(ctx) {
        await ctx.ring('[data-testid="small-appliance-circuits"]');
        await ctx.sleep(200);
        await ctx.ring('[data-testid="laundry-circuits"]');
      },
    },
    {
      id: 'trad-checklist',
      tab: 'ami',
      title: 'What is already in the house',
      body:
        'Everything fastened in place, permanently connected or on its own circuit goes on ' +
        'the list. Picking a quantity is all it takes: each item arrives with a standard ' +
        'rating, and a row appears below it to adjust from the nameplate.',
      invite: 'pick a quantity on anything else you like — watch a row appear for it.',
      async run(ctx) {
        for (const key of DEMO_INVENTORY) {
          await ctx.chooseOption(`[data-testid="checklist-${key}"]`, '1', (v) =>
            ctx.addExisting(demoInventorySeed(key, Number(v) || 1)),
          );
          await ctx.sleep(250);
        }
      },
      settle(ctx) {
        for (const key of DEMO_INVENTORY) ctx.addExisting(demoInventorySeed(key));
      },
    },
    {
      id: 'trad-defaults',
      tab: 'ami',
      title: 'Standard values are a starting point',
      body:
        'That blue tag means the value has not been checked against a nameplate yet, and ' +
        'the report says how many are left that way. Heating and cooling equipment gets no ' +
        'standard value at all — ratings vary too much — so those go in straight off the ' +
        'nameplate. A fuel-fired furnace still has a blower and controls, which is why it ' +
        'is here.',
      async run(ctx) {
        await ctx.chooseOption(`[data-testid="checklist-${DEMO_INVENTORY_HEATING}"]`, '1', (v) =>
          ctx.addExisting(demoInventorySeed(DEMO_INVENTORY_HEATING, Number(v) || 1)),
        );
        await ctx.sleep(400);
        await ctx.ring('[data-testid="existing-default-tag-0"]');
        await ctx.sleep(200);
      },
      settle(ctx) {
        ctx.addExisting(demoInventorySeed(DEMO_INVENTORY_HEATING));
      },
    },
    {
      id: 'trad-attest',
      tab: 'ami',
      title: 'Your word on the list',
      body:
        'The whole method rests on the inventory being complete, so this is its version of ' +
        'the data-quality flag: you attest that everything is listed, and the attestation ' +
        'prints above your signature. The subtotal is the raw connected load — the code’s ' +
        'discounts come next, on Results.',
      async run(ctx) {
        await ctx.ring('[data-testid="connected-subtotal"]');
        await ctx.sleep(200);
        await ctx.click('[data-testid="inventory-attested"]');
        ctx.patch({ inventory_attested: true });
        await ctx.sleep(400);
      },
      settle(ctx) {
        ctx.patch({ inventory_attested: true });
      },
    },
  ];
}

function traditionalResultsBeats(second: boolean): DemoBeat[] {
  return [
    second ? basisBeat('traditional') : verdictBeat(),
    {
      id: 'trad-method',
      tab: 'results',
      title: 'Which version of the method, and why',
      body:
        '220.83(B) — the B because a heat pump is being added. Version (A) is for jobs ' +
        'adding no heating or cooling, and treats the whole house as one pool. The scope ' +
        'line spells out why the section applies to this house at all: the first thing an ' +
        'inspector checks.',
      async run(ctx) {
        await ctx.ring('[data-testid="trad-citation"]');
        await ctx.sleep(200);
        await ctx.ring('[data-testid="trad-qualification"]');
      },
    },
    {
      id: 'trad-pool',
      tab: 'results',
      title: 'Everything in the house, added up',
      body:
        'Lighting from the floor area, the circuits, every appliance on the list and the ' +
        'new water heater, all at nameplate: 37.5 kVA of connected load. Nobody’s house ' +
        'draws that at once, and the code knows it.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="trad-row-pool"]');
        await ctx.ring('[data-testid="trad-row-pool"]');
        await ctx.sleep(300);
      },
    },
    {
      id: 'trad-tiers',
      tab: 'results',
      title: 'The code’s own discount',
      body:
        'The first 8 kVA count in full; everything past it counts at 40 percent. That is ' +
        'the section’s allowance for loads not all running together. Even so, the total ' +
        'usually lands above what a meter actually records — which is where measured ' +
        'demand earns its keep.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="trad-row-tier"]');
        await ctx.ring('[data-testid="trad-row-tier"]');
        await ctx.sleep(250);
        await ctx.ring('[data-testid="trad-row-remainder"]');
        await ctx.sleep(250);
      },
    },
    {
      id: 'trad-hvac',
      tab: 'results',
      title: 'Heating and cooling get no discount',
      body:
        'Anything that heats or cools the house sits on its own line at 100 percent under ' +
        'version (B) — here the new heat pump, 5.76 kVA in full. Where a house has both ' +
        'heating and cooling, only the larger of the two counts, since they never run ' +
        'together.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="trad-row-hvac"]');
        await ctx.ring('[data-testid="trad-row-hvac"]');
        await ctx.sleep(300);
      },
    },
    {
      id: 'trad-headroom',
      tab: 'results',
      title: 'How much room is left',
      body:
        'It comes to 25.6 kVA — 106.5 amps on a 200-amp service — with 22 kVA to spare. ' +
        'The amps are the figure you quote; the margin answers the customer’s next ' +
        'question, which is whether they can add something else later.',
      async run(ctx) {
        await ctx.moveTo('[data-testid="result-adjusted-demand"]');
        await ctx.ring('[data-testid="result-adjusted-demand"]');
        await ctx.sleep(250);
        await ctx.ring('[data-testid="result-margin"]');
        await ctx.sleep(300);
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Shared within a path — the new loads, and the first results beat.
// ---------------------------------------------------------------------------

function equipmentBeats(path: DemoPath): DemoBeat[] {
  const measured = path === 'measured';
  return [
    {
      id: 'equipment-intro',
      tab: 'equipment',
      title: 'Now — what are you adding?',
      body:
        (measured
          ? 'The measured peak tells you what the house already draws. '
          : 'The dwelling profile tells you what the house already carries. ') +
        'This tab is everything you are about to put on top of it. Empty right now, because ' +
        'nothing has been proposed yet.',
      async run(ctx) {
        await ctx.click('[data-testid="tab-equipment"]', { real: true });
        await ctx.sleep(600);
      },
    },
    {
      id: 'equipment-add',
      tab: 'equipment',
      title: 'One row per piece of equipment',
      body:
        'Add a row for each thing being installed. A blank one to start — name it whatever ' +
        'you will recognise on the report.',
      async run(ctx) {
        await ctx.click('[data-testid="btn-add-equipment"]');
        ctx.addEquipment();
        await ctx.sleep(500);
        await ctx.type('[data-testid="equipment-name-0"]', HEAT_PUMP.equipment_name!, (v) =>
          ctx.updateEquipment({ equipment_name: v }),
        );
      },
      settle(ctx) {
        ctx.addEquipment();
        ctx.updateEquipment({ equipment_name: HEAT_PUMP.equipment_name });
      },
    },
    {
      id: 'equipment-fill',
      tab: 'equipment',
      title: 'Its type and its rating',
      body:
        (measured
          ? 'Type tells TrueLoad how the load behaves through the year. '
          : 'Type tells TrueLoad which line of the code the load lands on — heating and ' +
            'cooling are treated differently from everything else. ') +
        'Take the amps off the nameplate rather than a spec sheet — nameplate is what an ' +
        'inspector will check against, and MCA is only for when there is no nameplate ' +
        'figure to be had.',
      async run(ctx) {
        await ctx.chooseOption('[data-testid="equipment-type-0"]', 'Heat Pump', (v) =>
          ctx.updateEquipment({ equipment_type: v as never }),
        );
        await ctx.sleep(420);
        await ctx.chooseOption('[data-testid="equipment-source-0"]', 'Nameplate', (v) =>
          ctx.updateEquipment({ source: v as never }),
        );
        await ctx.sleep(420);
        await ctx.type('[data-testid="equipment-amps-0"]', '24', (v) =>
          ctx.updateEquipment({ amps: Number(v) || null }),
        );
        await ctx.sleep(300);
      },
      settle(ctx) {
        ctx.updateEquipment({
          equipment_type: HEAT_PUMP.equipment_type,
          source: HEAT_PUMP.source,
          amps: HEAT_PUMP.amps,
          voltage: HEAT_PUMP.voltage,
        });
      },
    },
    measured
      ? {
          id: 'equipment-concurrency',
          tab: 'equipment',
          title: 'Does it run when the house is busiest?',
          body:
            'That is the whole question behind this column. The measured peak here landed on a ' +
            'winter evening, and a heat pump is working hardest exactly then — so it counts ' +
            'against the service in full. An air conditioner would not have; it peaks in July, ' +
            'when this house is quiet.',
          async run(ctx) {
            // Light the season first and leave it lit, then move to the column it
            // decided — both marks up at once is what makes the link land.
            await ctx.click('[data-testid="peak-season-word"]');
            await ctx.mark('[data-testid="peak-season-word"]', { tone: 'blue', shape: 'box' });
            await ctx.sleep(700);
            await ctx.mark('[data-testid="equipment-concurrent-0"]', { tone: 'blue', shape: 'box' });
            await ctx.sleep(400);
          },
        }
      : {
          id: 'equipment-no-concurrency',
          tab: 'equipment',
          title: 'No concurrency call to make here',
          body:
            'That column asks whether a load runs when the meter peaked — a measured-method ' +
            'question. There is no meter peak on this path, so the box stays locked and the ' +
            'traditional method applies the code’s own percentages instead. You will see them ' +
            'on the next tab.',
          async run(ctx) {
            await ctx.mark('[data-testid="equipment-concurrent-0"]', { tone: 'blue', shape: 'box' });
            await ctx.sleep(400);
          },
        },
    {
      id: 'equipment-second',
      tab: 'equipment',
      title: 'As many as the job needs',
      body:
        'Jobs often have more than one addition. Adding a heat pump water heater alongside it — ' +
        (measured
          ? 'each row is judged on its own, and the ones that run at peak are added together.'
          : 'each row is judged on its own, and lands on the code’s line for its type.'),
      invite: measured
        ? 'add a row of your own and pick a type — watch what it decides about concurrency.'
        : 'add a row of your own and pick a type — it joins the calculation on the next tab.',
      async run(ctx) {
        await ctx.click('[data-testid="btn-add-equipment"]');
        ctx.addEquipment();
        await ctx.sleep(500);
        await ctx.type('[data-testid="equipment-name-1"]', WATER_HEATER.equipment_name!, (v) =>
          ctx.updateEquipment({ equipment_name: v }),
        );
        await ctx.chooseOption('[data-testid="equipment-type-1"]', 'Water Heater', (v) =>
          ctx.updateEquipment({ equipment_type: v as never }),
        );
        await ctx.sleep(380);
        await ctx.chooseOption('[data-testid="equipment-source-1"]', 'Nameplate', (v) =>
          ctx.updateEquipment({ source: v as never }),
        );
        await ctx.sleep(380);
        await ctx.type('[data-testid="equipment-amps-1"]', '15', (v) =>
          ctx.updateEquipment({ amps: Number(v) || null }),
        );
      },
      settle(ctx) {
        ctx.addEquipment();
        ctx.updateEquipment(WATER_HEATER);
      },
    },
  ];
}

/** The first results beat of a first pass: open the tab, land the verdict. */
function verdictBeat(): DemoBeat {
  return {
    id: 'results-verdict',
    tab: 'results',
    title: 'The answer',
    body:
      'Everything so far comes down to one comparison: what the service can carry, ' +
      'against what it will be asked to carry. Here is where that lands — then we will ' +
      'take the numbers behind it one at a time.',
    async run(ctx) {
      await ctx.click('[data-testid="tab-results"]', { real: true });
      await ctx.sleep(800);
      await ctx.pulse('[data-testid="result-pass-fail"]');
      await ctx.sleep(300);
    },
  };
}

/**
 * The first results beat of a second pass. Both paths now hold data, so the
 * Results tab shows every method that ran and asks for a basis of record — and
 * this is where the newly added method becomes it.
 */
function basisBeat(path: DemoPath): DemoBeat {
  return {
    id: 'results-basis',
    tab: 'results',
    title: 'Two methods, one report',
    body:
      'With both paths holding data, Results shows every method that ran — including any ' +
      'that fail — and you choose the basis of record: the method the report, the ' +
      `completion check and the recorded verdict all follow. Setting ${DEMO_PATH_LABELS[path].toLowerCase()} ` +
      'as the basis brings its own summary up below. The report carries one method unless ' +
      'you ask for the comparison.',
    async run(ctx) {
      await ctx.click('[data-testid="tab-results"]', { real: true });
      await ctx.sleep(800);
      await ctx.waitFor('[data-testid="method-comparison"]', 3000);
      await ctx.click(`[data-testid="btn-basis-${path}"]`, { real: true });
      ctx.patch({ report_method: path, methodology_version: versionFor(path) });
      await ctx.sleep(700);
      await ctx.pulse('[data-testid="result-pass-fail"]');
      await ctx.sleep(300);
    },
    settle(ctx) {
      ctx.patch({ report_method: path, methodology_version: versionFor(path) });
    },
  };
}

/** Only ever after a second pass: the meter checking the inventory. */
function crossCheckBeat(): DemoBeat {
  return {
    id: 'results-crosscheck',
    tab: 'results',
    title: 'One number keeping the other honest',
    body:
      'Having both also lets the meter check the inventory. A house cannot draw more than ' +
      'is wired into it, so the metered peak is a floor under the connected load — here ' +
      'roughly a quarter of it, squarely in the normal 20 to 35 percent. Above 60 percent ' +
      'the list is almost certainly missing something.',
    async run(ctx) {
      await ctx.ring('[data-testid="inventory-cross-check"]');
      await ctx.sleep(400);
    },
  };
}

// ---------------------------------------------------------------------------
// Stage 4 — the offer to see the other method on the same house.
// ---------------------------------------------------------------------------

function offerBeat(first: DemoPath): DemoBeat {
  return {
    id: 'offer',
    tab: 'results',
    awaits: 'offer',
    title: 'That is the whole answer, this way',
    body:
      (first === 'measured'
        ? 'Measured demand found room. The same house can also be run through the ' +
          'traditional calculation — it usually lands higher, '
        : 'The traditional calculation found room. The same house can also be run through ' +
          'measured demand — it usually lands lower, ') +
      'and seeing both side by side is how you learn when each one earns its keep. Or go ' +
      'straight to the report.',
    async run(ctx) {
      await ctx.sleep(300);
    },
  };
}

// ---------------------------------------------------------------------------
// Stage 5 — the shared ending: the readiness check, the report, the wrap-up.
// ---------------------------------------------------------------------------

function endingBeats(basis: DemoPath, seen: DemoPath[]): DemoBeat[] {
  const both = seen.length === 2;
  return [
    {
      id: 'report-check',
      tab: 'results',
      title: 'Generating it',
      body:
        'Generate Report runs a readiness check first and shows you what it found, so ' +
        'nothing is missing by surprise. Nothing is ever withheld — an unfinished job still ' +
        'prints, stamped DRAFT, so it cannot be mistaken for a final one. Have a read: this ' +
        'job is complete, so it will go straight through' +
        (basis === 'traditional'
          ? ' — with one warning to read on the way, the standard values nobody checked ' +
            'against a nameplate. The report discloses them.'
          : '.'),
      keeps: ['validation-modal'],
      async run(ctx) {
        await ctx.click('[data-testid="btn-generate-report"]', { real: true });
        await ctx.waitFor('[data-testid="validation-modal"]', 3000);
        await ctx.sleep(600);
      },
    },
    {
      id: 'report',
      tab: 'results',
      title: 'The thing you actually hand over',
      body:
        'Methodology, the data behind it, the equipment schedule, the result and your ' +
        'details — laid out for whoever has to read it, whether that is an inspector, a ' +
        'utility, or the customer deciding whether to go ahead.',
      keeps: ['validation-modal', 'report-modal'],
      invite: 'scroll through it — this is the document that leaves your hands.',
      async run(ctx) {
        await ensureReportOpen(ctx, true);
        await ctx.sleep(700);
      },
    },
    {
      id: 'report-actions',
      tab: 'results',
      title: 'Getting it to whoever needs it',
      body:
        'Print it for the permit packet, download the PDF and email it, or share it ' +
        'straight from your phone on site. Same document either way.',
      keeps: ['report-modal'],
      // The action bar runs along the bottom edge, where the card lives.
      card: 'top',
      async run(ctx) {
        await ensureReportOpen(ctx, false);
        // The buttons, not the whole footer they sit in — an oval around the
        // full-width bar is enormous and points at nothing in particular.
        await ctx.mark('[data-testid="report-action-buttons"]', { tone: 'red', shape: 'oval' });
        await ctx.sleep(300);
      },
    },
    {
      id: 'finish',
      tab: 'results',
      title: 'That is the whole job',
      body:
        'Address, service rating, ' +
        (both
          ? 'the existing load worked out both ways'
          : basis === 'measured'
            ? 'a year of meter data'
            : 'the house and what is in it') +
        ', what you are adding, an answer you can hand over. Your turn — and the Help tab ' +
        'can replay this any time.',
      async run(ctx) {
        await ctx.sleep(200);
      },
    },
  ];
}

// ---------------------------------------------------------------------------

/** One path's beats: existing load, new loads (first pass only), results. */
function pathBeats(path: DemoPath, second: boolean): DemoBeat[] {
  const existing =
    path === 'measured' ? measuredExistingLoadBeats(second) : traditionalExistingLoadBeats(second);
  const results = path === 'measured' ? measuredResultsBeats(second) : traditionalResultsBeats(second);
  // The two new loads are already on the job by the second pass.
  return [...existing, ...(second ? [] : equipmentBeats(path)), ...results];
}

/**
 * The whole script for a plan. Undecided, it runs as far as the fork; the rest
 * is appended as the viewer chooses, and nothing before their choice moves.
 */
export function buildDemoScript(seed: DemoSeed, plan: DemoPlan): DemoBeat[] {
  const beats: DemoBeat[] = [...trunkBeats(seed), forkBeat(plan)];
  if (!plan.first) return beats;

  beats.push(...pathBeats(plan.first, false), offerBeat(plan.first));

  const seen: DemoPath[] = [plan.first];
  if (plan.second && plan.second !== plan.first) {
    beats.push(...pathBeats(plan.second, true), crossCheckBeat());
    seen.push(plan.second);
  }
  beats.push(...endingBeats(plan.second ?? plan.first, seen));
  return beats;
}
